<template>
  <div class="breadcrumb-container" v-if="breadcrumbList.length > 0">
    <div class="breadcrumb" :class="breadcrumbClass">
      <template v-for="(item, index) in breadcrumbList">
        <router-link 
          v-if="item.path && index < breadcrumbList.length - 1" 
          :to="getBreadcrumbRoute(item.path)" 
          :key="`link-${index}`"
          class="breadcrumb-link"
        >
          {{ item.title }}
        </router-link>
        <span v-else :key="`text-${index}`" class="breadcrumb-current">
          {{ item.title }}
        </span>
        <span v-if="index < breadcrumbList.length - 1" :key="`separator-${index}`" class="breadcrumb-separator">
          &gt;
        </span>
      </template>
    </div>
  </div>
</template>

<script>
import { findMenuByPath, menuList } from '@/common/menuConfig'

export default {
  name: 'Breadcrumb',
  props: {
    // 自定义面包屑数据，如果提供则使用自定义数据
    items: {
      type: Array,
      default: null
    },
    // 是否使用浅色样式（用于前台页面）
    light: {
      type: Boolean,
      default: false
    }
  },
  computed: {
    breadcrumbClass() {
      return {
        'breadcrumb-light': this.light
      }
    },
    breadcrumbList() {
      // 如果提供了自定义数据，直接使用
      if (this.items && this.items.length > 0) {
        return this.items
      }
      
      const route = this.$route
      
      // 构建面包屑列表
      const breadcrumbs = []
      
      // 首页总是第一个（除了首页本身）
      if (route.path !== '/') {
        breadcrumbs.push({
          title: '首页',
          path: '/'
        })
      }
      
      // 处理当前路由
      let currentPath = route.path
      let currentQuery = route.query || {}
      
      // 处理动态路由参数（如 /party/detail/:id）
      if (route.params && Object.keys(route.params).length > 0) {
        // 检查是否是详情页
        if (currentPath.includes('/detail')) {
          // 提取基础路径（如 /party/detail）
          const pathParts = currentPath.split('/')
          const detailIndex = pathParts.indexOf('detail')
          if (detailIndex > 0) {
            currentPath = '/' + pathParts.slice(1, detailIndex + 1).join('/')
          }
        } else {
          // 移除最后一个参数段
          currentPath = route.path.split('/').slice(0, -1).join('/') || '/'
        }
      }
      
      // 从导航菜单中查找匹配的菜单项
      let matchedMenu = null
      
      // 先尝试精确匹配（包括查询参数）- 优先匹配带查询参数的菜单
      matchedMenu = findMenuByPath(currentPath, currentQuery)
      
      // 如果没有匹配，尝试匹配父路径
      if (!matchedMenu) {
        const pathSegments = currentPath.split('/').filter(Boolean)
        for (let i = pathSegments.length; i > 0; i--) {
          const testPath = '/' + pathSegments.slice(0, i).join('/')
          matchedMenu = findMenuByPath(testPath, currentQuery)
          if (matchedMenu) {
            break
          }
        }
      }
      
      // 检查是否匹配到带查询参数的菜单（如政策指南）
      const hasQueryParamMenu = matchedMenu && matchedMenu.path.includes('?')
      
        // 特殊处理详情页：需要找到父级菜单
        if (route.path.includes('/detail') || currentPath.includes('/detail')) {
          // 提取父路径（从原始路径中提取，而不是处理后的路径）
          let parentPath = route.path
          if (parentPath.includes('/detail')) {
            // 特殊处理：/recruitment/detail -> /open_affairs
            if (parentPath === '/recruitment/detail' || parentPath.startsWith('/recruitment/detail')) {
              parentPath = '/open_affairs'
            }
            // 特殊处理：/policy_guide/detail -> /news（政策指南）
            else if (parentPath === '/policy_guide/detail' || parentPath.startsWith('/policy_guide/detail')) {
              parentPath = '/news'
            }
            // 特殊处理：/company/detail -> /discipline_inspection（找工作）
            else if (parentPath === '/company/detail' || parentPath.startsWith('/company/detail')) {
              parentPath = '/discipline_inspection'
            }
            else {
              const pathParts = parentPath.split('/').filter(Boolean) // 过滤空字符串
              const detailIndex = pathParts.indexOf('detail')
              if (detailIndex > 0) {
                // 提取 detail 之前的所有路径段
                parentPath = '/' + pathParts.slice(0, detailIndex).join('/')
              } else {
                parentPath = parentPath.replace('/detail', '')
              }
            }
          }
          
          // 处理路径别名：/supervision 对应 /discipline_inspection
          if (parentPath === '/supervision') {
            parentPath = '/discipline_inspection'
          }
        
        // 调试：输出提取的父路径（开发时可以启用）
        // console.log('详情页父路径:', parentPath, '原始路径:', route.path, '查询参数:', route.query)
        
        // 查找父级菜单
        let parentMenu = null
        
        // 对于 /news/detail 和 /policy_guide/detail，需要特殊处理：检查查询参数或来源路由
        // 注意：/news/detail 是共用页面，需要通过 cid 来区分不同的菜单
        // - 通知公告的 cid 是 1052
        // - 政策指南的 cid 是 1052（但 path 是 /news?type=8）
        // - 招聘会的 cid 是 1045（但 path 是 /open_affairs）
        // 注意：/policy_guide/detail 是政策指南的专用详情页
        // 注意：/recruitment/detail 是招聘会的专用详情页
        if (parentPath === '/news') {
          // 如果当前路径是 /policy_guide/detail，则一定是政策指南
          if (route.path.includes('/policy_guide/detail')) {
            parentMenu = findMenuByPath(parentPath, { type: '8' })
          }
          // 优先检查查询参数中的 type=8（政策指南）
          if (route.query && (route.query.type === '8' || route.query.type === 8)) {
            parentMenu = findMenuByPath(parentPath, { type: '8' })
          }
          
          // 如果没有 type 参数，通过 cid 查找对应的菜单
          if (!parentMenu && route.query && route.query.cid) {
            const cid = String(route.query.cid)
            // 通过 cid 查找菜单（可能跨路径，如招聘会 cid: 1045 对应 /open_affairs）
            const menuByCid = menuList.find(m => {
              if (!m.cid) return false
              return String(m.cid) === cid
            })
            if (menuByCid) {
              parentMenu = menuByCid
            }
          }
          
          // 如果没有 cid 参数，检查来源路由（从 sessionStorage 获取）
          if (!parentMenu) {
            try {
              const lastRoutePath = sessionStorage.getItem('lastRoutePath') || ''
              const lastRouteQueryStr = sessionStorage.getItem('lastRouteQuery') || '{}'
              const lastRouteQuery = JSON.parse(lastRouteQueryStr)
              
              // 如果来源路由是政策指南，则详情页也是政策指南
              if (lastRoutePath === '/news' && (lastRouteQuery.type === '8' || lastRouteQuery.type === 8)) {
                parentMenu = findMenuByPath(parentPath, { type: '8' })
              }
              // 如果来源路由是其他路径（如 /open_affairs），通过路径查找菜单
              else if (lastRoutePath && lastRoutePath !== '/news') {
                const menuByPath = menuList.find(m => {
                  const menuPath = m.path.includes('?') ? m.path.split('?')[0] : m.path
                  return menuPath === lastRoutePath
                })
                if (menuByPath) {
                  parentMenu = menuByPath
                }
              }
            } catch (e) {
              // 忽略解析错误
            }
          }
          
          // 如果还是没找到，默认匹配通知公告（不带查询参数的 /news）
          if (!parentMenu) {
            parentMenu = findMenuByPath(parentPath, {})
          }
        } else {
          // 对于其他路径的详情页，优先通过路径直接匹配菜单（最准确）
          // 特殊处理：/recruitment/detail 的父路径是 /open_affairs（招聘会）
          if (parentPath === '/open_affairs' && route.path.includes('/recruitment/detail')) {
            parentMenu = findMenuByPath(parentPath, {})
          }
          // 直接从 menuList 中查找路径匹配的菜单，避免通过 findMenuByPath 可能的问题
          const menusByPath = menuList.filter(m => {
            const menuPath = m.path.includes('?') ? m.path.split('?')[0] : m.path
            // 严格匹配路径，不能跨路径匹配
            return menuPath === parentPath
          })
          
          // 调试：输出匹配的菜单
          // console.log('路径匹配结果:', parentPath, '匹配到的菜单:', menusByPath.map(m => m.title))
          
          if (menusByPath.length > 0) {
            // 如果找到多个匹配的菜单（如 /news 有通知公告和政策指南），通过 cid 或查询参数区分
            if (menusByPath.length > 1 && route.query && route.query.cid) {
              const menuByCid = menusByPath.find(m => {
                if (!m.cid) return false
                return String(m.cid) === String(route.query.cid)
              })
              parentMenu = menuByCid || menusByPath[0]
            } else {
              // 只有一个匹配的菜单，直接使用（这是最常见的情况，如 /open_affairs 只匹配招聘会）
              parentMenu = menusByPath[0]
            }
          } else {
            // 如果路径匹配失败，尝试通过 findMenuByPath 查找
            parentMenu = findMenuByPath(parentPath, {})
            
            // 如果还是没找到，尝试通过查询参数查找
            if (!parentMenu && route.query && Object.keys(route.query).length > 0) {
              parentMenu = findMenuByPath(parentPath, route.query)
            }
            
            // 如果还是没找到，尝试通过 cid 查找（作为最后备选方案）
            // 注意：必须确保路径也匹配，避免跨路径匹配
            // 重要：这一步可能会导致跨路径匹配，所以必须严格检查路径
            if (!parentMenu && route.query && route.query.cid) {
              // 先通过路径找到所有可能的菜单
              const menusByPathForCid = menuList.filter(m => {
                const menuPath = m.path.includes('?') ? m.path.split('?')[0] : m.path
                return menuPath === parentPath
              })
              
              // 在路径匹配的菜单中，通过 cid 查找
              if (menusByPathForCid.length > 0) {
                const menuByCid = menusByPathForCid.find(m => {
                  if (!m.cid) return false
                  return String(m.cid) === String(route.query.cid)
                })
                if (menuByCid) {
                  parentMenu = menuByCid
                } else {
                  // 如果 cid 不匹配，使用路径匹配的第一个菜单
                  parentMenu = menusByPathForCid[0]
                }
              }
            }
          }
        }
        
        if (parentMenu) {
          // 添加父级菜单到面包屑
          // 注意：对于带查询参数的菜单（如政策指南），需要保留完整的路径和查询参数
          breadcrumbs.push({
            title: parentMenu.title,
            path: parentMenu.path
          })
          
          // 添加详情页（当前页）：显示为"当前nav名称详情"
          breadcrumbs.push({
            title: parentMenu.title + '详情',
            path: null
          })
        } else {
          // 如果找不到父级菜单，使用默认的详情标题
          breadcrumbs.push({
            title: (route.meta && route.meta.title) || '详情',
            path: null
          })
        }
      } else if (matchedMenu) {
        // 如果找到了匹配的菜单项，添加到面包屑
        breadcrumbs.push({
          title: matchedMenu.title,
          path: matchedMenu.path.includes('?') ? matchedMenu.path.split('?')[0] : matchedMenu.path
        })
      } else if (route.meta && route.meta.title && route.path !== '/') {
        // 如果路由有 meta.title，使用它
        breadcrumbs.push({
          title: route.meta.title,
          path: route.path
        })
      }
      
      // 处理查询参数中的类型信息（用于动态标题）
      // 重要：如果已经匹配到带查询参数的菜单（如政策指南），绝对不应该覆盖标题
      if (route.query && route.query.type && !currentPath.includes('/detail') && !hasQueryParamMenu) {
        const lastItem = breadcrumbs[breadcrumbs.length - 1]
        if (lastItem) {
          if (lastItem.path === null) {
            // 如果最后一项没有路径，添加类型信息
            breadcrumbs.push({
              title: route.query.type,
              path: null
            })
          } else {
            // 更新最后一项的标题（仅当不是带查询参数的菜单时）
            lastItem.title = route.query.type
          }
        }
      }
      
      return breadcrumbs
    }
  },
  methods: {
    // 处理面包屑路由，支持带查询参数的路径
    getBreadcrumbRoute(path) {
      if (path.includes('?')) {
        // 如果路径包含查询参数，解析为路由对象
        const [routePath, queryStr] = path.split('?')
        const query = {}
        const queryParams = new URLSearchParams(queryStr)
        for (const [key, value] of queryParams.entries()) {
          query[key] = value
        }
        return { path: routePath, query }
      }
      return path
    }
  }
}
</script>

<style scoped>
.breadcrumb-container {
  margin-bottom: 20px;
  padding-bottom: 10px;
  border-bottom: 1px solid #e9ecef;
}

.breadcrumb {
  font-size: 14px;
  text-align: right;
  display: flex;
  align-items: center;
  justify-content: flex-end;
  flex-wrap: wrap;
}

.breadcrumb-link {
  color: #007bff;
  text-decoration: none;
  transition: color 0.3s;
}

.breadcrumb-link:hover {
  color: #0056b3;
  text-decoration: underline;
}

.breadcrumb-current {
  color: #666;
}

.breadcrumb-separator {
  margin: 0 8px;
  color: #999;
}

/* 浅色样式（用于前台页面） */
.breadcrumb-light .breadcrumb-link,
.breadcrumb-light .breadcrumb-current,
.breadcrumb-light .breadcrumb-separator {
  color: #ccc;
}

.breadcrumb-light .breadcrumb-link:hover {
  color: #fff;
  text-decoration: underline;
}
</style>



// WEBPACK FOOTER //
// src/components/Breadcrumb.vue